import { css } from '@emotion/react'
import styled from '@emotion/styled'
import { m } from 'framer-motion'
import { useRouter } from 'next/router'
import React from 'react'

import AnimatedBorder from 'components/AnimatedBorder'
import Button from 'components/Button'
import useTemplateId from 'hooks/useTemplateId'
import { useTracking } from 'hooks/useTracking'
import { getSnippetCode } from 'lib/snippetCode'
import { EVENT_NAMES } from 'lib/tracking'
import { progress } from 'styles/animations'

const ProgressBar = styled.div`
	height: 7px;
	width: 0;
	background-color: ${({ theme }) => theme.colors.light.border};
	position: absolute;
	bottom: 0;
	left: 1px;
	animation: ${progress} 5s forwards linear;
	border-end-end-radius: ${({ theme }) => theme.borderRadiuses.xl};
	border-end-start-radius: ${({ theme }) => theme.borderRadiuses.xl};
`

const PasteTarget = styled(m.div)`
	width: 140px;
	height: 55px;
	top: ${({ theme }) => (theme.isMobile ? '75px' : '95px')};
	left: ${({ theme }) => (theme.isMobile ? '50px' : '60px')};
	display: grid;
	place-items: center;
	position: absolute;

	div {
		${({ theme }) => theme.typography.captionMedium};
		position: absolute;
		left: 26px;
		top: 18px;
	}
`

const Code = styled.textarea`
	height: 160px;
	width: 100%;
	outline: 0px;
	border: none;
	resize: none;
	pointer-events: none;
	background-color: transparent;
	color: #696969;
	${({ theme }) => theme.typography.code}
	line-height: 16px;
	text-align: left;
	white-space: pre-wrap;
	word-wrap: break-word;
	word-break: break-word;
	overflow: hidden;
`
const CodeCard = styled(m.div)`
	padding: ${({ theme }) => (theme.isMobile ? `0 ${theme.spaces.space3}` : `0 32px 0 ${theme.spaces.space6}`)};
	background-color: ${({ theme }) => theme.colors.light.primaryBackgroundColor};
	display: flex;
	justify-content: center;
	align-items: center;
	width: min(90%, 700px);
	border-top-left-radius: ${({ theme }) => theme.borderRadiuses.xl};
	border-top-right-radius: ${({ theme }) => theme.borderRadiuses.xl};
	border-bottom-left-radius: ${({ theme }) => (theme.isMobile ? theme.borderRadiuses.xl : 0)};
	border-bottom-right-radius: ${({ theme }) => (theme.isMobile ? theme.borderRadiuses.xl : 0)};
	box-shadow: ${({ theme }) => theme.boxShadows.floating};
`

const CodeWrapper = styled.div`
	padding: ${({ theme }) => theme.spaces.space6};
	display: flex;
	flex-flow: row wrap;
	align-items: center;
	border-radius: ${({ theme }) => theme.borderRadiuses.xl};
	background: linear-gradient(180deg, #c7c7c7 0%, #e6e6e6 100%);
	overflow: hidden;
	min-height: 240px;
	max-height: 240px;
	position: relative;
	overflow-x: hidden;
	justify-content: center;
	gap: 30px;
`

const CtaOuter = styled.div`
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	justify-content: center;
	${({ theme }) => theme.typography.caption};
	width: min(90%, 700px);
	margin-top: 0;
	gap: ${({ theme }) => theme.spaces.space5};
`

const Tag = styled(m.span)`
	display: inline;
	${({ theme }) => theme.typography.captionMedium}
	color: #0317fc;
	font-family: monospace;
`

const BodyTagRowWrapper = styled(m.div)`
	display: flex;
	align-items: center;
	gap: ${({ theme }) => theme.spaces.space2};
`

const buttonVariants = {
	initial: {
		width: 130,
		color: '',
		backgroundColor: '',
	},
	copied: {
		backgroundColor: '#268750',
		color: '#FFFFFF',
		width: 160,
	},
}

const buttonTextVariants = {
	initial: { translateX: 100, opacity: 0 },
	visible: { translateX: 0, opacity: 1 },
}

const pasteTargetOuterVariants = {
	visible: { opacity: 1, transition: { delay: 0.2 } },
	hidden: { opacity: 0, transition: { duration: 0.1 } },
}

const pasteTargetInnerVariants = {
	initial: { opacity: 0 },
	visible: { opacity: 1, translateY: 0 },
	hidden: { translateY: 20, opacity: 0 },
}

const codeCardVariants = {
	hidden: { translateY: 200 },
	visible: { translateY: 0, transition: { delay: 0.2 } },
}

const htmlTagVariants = {
	visible: { scale: 1.4, opacity: 1, translateY: 10, translateX: 15, display: 'inline' },
	hidden: {
		scale: 1,
		opacity: 0,
		display: 'none',
		transition: {
			duration: 0.2,
		},
	},
}

const bodyTagVariants = {
	visible: { scale: 1.4, paddingLeft: 25 },
	hidden: { scale: 1 },
}

const innerTextVariants = {
	visible: {
		opacity: 1,
	},
	hidden: {
		opacity: 0,
		display: 'none',
		transition: { duration: 0.1 },
	},
}

const outerTextVariants = {
	hide: { translateY: 100 },
	visible: { translateY: 0 },
}

type Props = {
	organizationId: string
	isPrimaryAction: boolean
}

const AnimatedCodeWrapper = ({ organizationId, isPrimaryAction }: Props) => {
	const track = useTracking()
	const router = useRouter()
	const { templateId } = useTemplateId()
	const codeRef = React.useRef<HTMLTextAreaElement>(null)
	const [showCopySuccess, setShowCopySuccess] = React.useState(false)

	const handleCopyCode = async () => {
		if (!codeRef.current) {
			return
		}

		try {
			await navigator.clipboard.writeText(codeRef.current.value)
		} catch {
			codeRef.current.select()
			document.execCommand('copy')
			codeRef.current.setSelectionRange(0, 0)
			codeRef.current.blur()
		}
		setShowCopySuccess(true)
		track(EVENT_NAMES.CODE_COPIED)
		setTimeout(() => setShowCopySuccess(false), 5000)
	}

	const snippetCode = React.useMemo(
		() =>
			getSnippetCode({
				organizationId,
				integrationName: router.asPath.includes('share') ? 'manual_share_page' : 'manual_settings',
				...(templateId && templateId !== organizationId && { templateId }),
			}),
		[organizationId, router.asPath, templateId],
	)

	return (
		<CodeWrapper data-testid="code-wrapper">
			<CtaOuter>
				<Button
					variants={buttonVariants}
					initial="initial"
					animate={showCopySuccess ? 'copied' : 'initial'}
					type="button"
					disabled={showCopySuccess}
					onClick={handleCopyCode}
					kind={isPrimaryAction ? 'primary' : 'secondary'}
					css={css({ userSelect: 'none' })}
				>
					{showCopySuccess ? (
						<m.span variants={buttonTextVariants} initial="initial" animate="visible">
							Code copied!
						</m.span>
					) : (
						<span>Copy code</span>
					)}
				</Button>
				<PasteTarget variants={pasteTargetOuterVariants} animate={showCopySuccess ? 'visible' : 'hidden'}>
					<m.div
						variants={pasteTargetInnerVariants}
						initial={'initial'}
						animate={showCopySuccess ? 'visible' : 'hidden'}
						transition={{ duration: 1, ease: 'easeOut' }}
					>
						Paste it here!
					</m.div>
					<AnimatedBorder showAnimation={showCopySuccess} />
				</PasteTarget>
				<m.div
					variants={outerTextVariants}
					transition={{ ease: 'easeOut', duration: 0.4 }}
					animate={showCopySuccess ? 'hide' : 'visible'}
				>
					<m.span variants={innerTextVariants} animate={showCopySuccess ? 'hidden' : 'visible'}>
						and paste it before the closing
					</m.span>
					<BodyTagRowWrapper>
						<Tag variants={bodyTagVariants} animate={showCopySuccess ? 'visible' : 'hidden'}>{`</body>`}</Tag>
						<m.span variants={innerTextVariants} animate={showCopySuccess ? 'hidden' : 'visible'}>
							tag to install on your website.
						</m.span>
					</BodyTagRowWrapper>
					<Tag
						variants={htmlTagVariants}
						css={showCopySuccess && css({ position: 'absolute' })}
						animate={showCopySuccess ? 'visible' : 'hidden'}
					>{`</html>`}</Tag>
				</m.div>
			</CtaOuter>
			<CodeCard variants={codeCardVariants} animate={showCopySuccess ? 'hidden' : 'visible'}>
				{organizationId && <Code ref={codeRef} readOnly value={snippetCode} />}
			</CodeCard>
			{showCopySuccess && <ProgressBar />}
		</CodeWrapper>
	)
}

export default AnimatedCodeWrapper
